import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAppToast } from '../ui/useAppToast';
import { describeAcceptRefusal } from '../../utils/invitationAcceptFailure';
import {
  INVITATION_REFUSAL_EVENT,
  keepPendingInvitationToken,
  takeInvitationRefusal,
} from '../../utils/pendingInvitation';

/**
 * Tells a Google/Microsoft invitee why the server refused their invite
 * (`recordInvitationRefusal`), with the same copy AuthRedirect shows, and keeps
 * the token when the refusal is retryable (a seat cap) so the next sign-in or
 * invite-link visit can accept it.
 *
 * Mounted in the authenticated app, never on `/auth`, so the restored token
 * cannot reach an AuthRedirect still racing the sign-in's own accept.
 */
export function usePendingInvitationRefusalToast(): void {
  const { user } = useAuth();
  const toast = useAppToast();
  const identity = user?.uid ?? null;
  const [recorded, setRecorded] = useState(0);

  useEffect(() => {
    const onRecorded = () => setRecorded((n) => n + 1);
    window.addEventListener(INVITATION_REFUSAL_EVENT, onRecorded);
    return () => window.removeEventListener(INVITATION_REFUSAL_EVENT, onRecorded);
  }, []);

  useEffect(() => {
    if (!identity) return;
    const refusal = takeInvitationRefusal(identity);
    if (!refusal) return;
    const presentation = describeAcceptRefusal({
      error: refusal.message,
      errorCause: refusal.cause,
    });
    if (presentation.keepToken) keepPendingInvitationToken(refusal.token);
    toast(presentation.toast);
  }, [identity, recorded, toast]);
}
